import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CalendarDays, Plus, Trash2, Repeat } from 'lucide-react';

const DAYS = [
  { idx: 0, label: 'Mon' }, { idx: 1, label: 'Tue' }, { idx: 2, label: 'Wed' },
  { idx: 3, label: 'Thu' }, { idx: 4, label: 'Fri' }, { idx: 5, label: 'Sat' }, { idx: 6, label: 'Sun' },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function AdminHolidayCalendarPage() {
  const [cfg, setCfg] = useState(null);
  const [newDate, setNewDate] = useState(todayISO());
  const [newLabel, setNewLabel] = useState('');
  const [newRecurring, setNewRecurring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingWeekly, setSavingWeekly] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/calendar');
      setCfg(r.data);
    } catch (e) { toast.error(parseErr(e)); }
  };

  useEffect(() => { load(); }, []);

  const addHoliday = async () => {
    if (!newDate || !newLabel.trim()) return;
    setSaving(true);
    try {
      await api.post('/admin/calendar/holidays', {
        date: newDate, label: newLabel.trim(), is_recurring: newRecurring,
      });
      toast.success('Holiday added.');
      setNewLabel('');
      setNewRecurring(false);
      load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  const removeHoliday = async (dateISO) => {
    if (!window.confirm(`Remove holiday for ${dateISO}?`)) return;
    try {
      await api.delete(`/admin/calendar/holidays/${dateISO}`);
      toast.success('Holiday removed.');
      load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  const toggleWeeklyOff = async (dayIdx) => {
    if (!cfg) return;
    const current = new Set(cfg.weekly_off || []);
    if (current.has(dayIdx)) current.delete(dayIdx);
    else current.add(dayIdx);
    if (current.size >= 7) {
      toast.error('At least one day must remain a working day.');
      return;
    }
    setSavingWeekly(true);
    try {
      await api.put('/admin/calendar/weekly_off', { weekly_off: Array.from(current).sort() });
      toast.success('Weekly off updated.');
      load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSavingWeekly(false); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Admin', to: '/admin/settings' }, { label: 'Holiday Calendar' }]}>
      <div className="p-6 max-w-[900px] mx-auto space-y-4 fade-up" data-testid="holiday-calendar-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <CalendarDays size={22} />Holiday Calendar
          </h1>
          <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[640px]">
            Weekends and dated holidays are excluded from working-day delay calculations everywhere in the app.
            Only owners can edit this. Changes take effect immediately.
          </p>
        </div>

        <section className="card p-4" data-testid="holiday-weekly-off">
          <div className="font-heading font-semibold text-[15px] mb-1">Weekly off-days</div>
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mb-3">
            Days flagged here do not count as working days.
          </div>
          <div className="flex flex-wrap gap-2">
            {DAYS.map(d => {
              const on = (cfg?.weekly_off || []).includes(d.idx);
              return (
                <button key={d.idx} onClick={() => toggleWeeklyOff(d.idx)} disabled={savingWeekly}
                        data-testid={`holiday-weekly-${d.label.toLowerCase()}`}
                        className={`px-3 py-1.5 rounded-md border text-[13px] transition-colors ${on ? 'bg-[hsl(var(--surface-2))] font-semibold' : ''}`}
                        style={{ borderColor: on ? 'hsl(var(--primary))' : 'hsl(var(--border))', color: on ? 'hsl(var(--primary))' : undefined }}>
                  {d.label}
                  {on && <span className="ml-1">✓</span>}
                </button>
              );
            })}
          </div>
        </section>

        <section className="card p-4" data-testid="holiday-add-form">
          <div className="font-heading font-semibold text-[15px] mb-3">Add a holiday</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Date</div>
              <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} data-testid="holiday-add-date" />
            </div>
            <div className="md:col-span-2">
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Label</div>
              <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Republic Day" data-testid="holiday-add-label" />
            </div>
            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-2 text-[13px] mb-2">
                <input type="checkbox" checked={newRecurring} onChange={e => setNewRecurring(e.target.checked)} data-testid="holiday-add-recurring" />
                <Repeat size={12} />Recurs yearly
              </label>
              <Button onClick={addHoliday} disabled={!newDate || !newLabel.trim() || saving} data-testid="holiday-add-submit">
                <Plus size={13} className="mr-1" />{saving ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </div>
        </section>

        <section className="card p-4" data-testid="holiday-list">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold text-[15px]">Holidays</div>
            <span className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">{cfg?.holidays?.length || 0}</span>
          </div>
          {!cfg && (
            <div className="text-[13px] text-[hsl(var(--foreground-muted))] italic" data-testid="holiday-list-loading">Loading holidays…</div>
          )}
          {cfg && (cfg.holidays?.length || 0) === 0 && (
            <div className="text-[13px] text-[hsl(var(--foreground-muted))] italic">No holidays configured yet.</div>
          )}
          <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
            {(cfg?.holidays || []).map(h => (
              <li key={h.date} className="py-2 flex items-center justify-between" data-testid={`holiday-row-${h.date}`}>
                <div className="min-w-0">
                  <div className="text-[14px] font-medium">{h.label}</div>
                  <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                    {h.date}
                    {h.is_recurring && <span className="chip chip-info ml-2"><Repeat size={10} className="inline mr-0.5" />Recurs yearly</span>}
                  </div>
                </div>
                <button className="p-2 rounded hover:bg-[hsl(var(--surface-2))] text-[hsl(var(--danger))]"
                        onClick={() => removeHoliday(h.date)}
                        data-testid={`holiday-remove-${h.date}`}
                        title="Remove holiday">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AppShell>
  );
}
